import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import MapIcon from "@mui/icons-material/Map";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import SettingsIcon from "@mui/icons-material/Settings";
import { updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { MuiTelInput } from "mui-tel-input";
import { v4 as uuidv4 } from "uuid";
import PropertyContext from "../contexts/PropertyContext";
import { propertyDoc } from "../utils/firestorePaths";
import PropertyMap from "./PropertyMap";

const DAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday",
  "Friday", "Saturday", "Sunday",
];

const TIMEZONES = [
  { label: "Eastern (ET) — New York", value: "America/New_York" },
  { label: "Central (CT) — Chicago", value: "America/Chicago" },
  { label: "Mountain (MT) — Denver", value: "America/Denver" },
  { label: "Mountain (MT) — Phoenix (no DST)", value: "America/Phoenix" },
  { label: "Pacific (PT) — Los Angeles", value: "America/Los_Angeles" },
  { label: "Alaska (AKT) — Anchorage", value: "America/Anchorage" },
  { label: "Hawaii (HT) — Honolulu", value: "Pacific/Honolulu" },
];

const defaultDaySchedule = {
  startTime: "08:00",
  endTime: "22:00",
  daytimeListId: "",
  afterHoursListId: "",
};

const buildDefaultSchedule = () =>
  Object.fromEntries(DAYS.map((d) => [d.toLowerCase(), { ...defaultDaySchedule }]));

const normalizeContacts = (contacts) =>
  (contacts || []).map((c) => ({
    linkedWith: null,
    ...c,
    id: c.id || uuidv4(),
  }));

// Build rows for rendering: consecutive linked pairs → shown side-by-side
const buildRows = (contacts) => {
  const seen = new Set();
  const rows = [];
  for (const contact of contacts) {
    if (seen.has(contact.id)) continue;
    seen.add(contact.id);
    if (contact.linkedWith) {
      const partner = contacts.find((c) => c.id === contact.linkedWith);
      if (partner && !seen.has(partner.id)) {
        seen.add(partner.id);
        rows.push({ type: "pair", contacts: [contact, partner] });
        continue;
      }
    }
    rows.push({ type: "single", contact });
  }
  return rows;
};

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

// ── ContactFields ──────────────────────────────────────────────────────────────
// Shared fields used in both single and pair card layouts.

const ContactFields = ({ contact, onUpdate }) => (
  <Grid
    container
    spacing={1.5}
    onMouseDown={(e) => e.stopPropagation()} // prevent drag from triggering on input focus
  >
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth size="small" label="Name"
        value={contact.name}
        onChange={(e) => onUpdate("name", e.target.value)}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth size="small" label="Role / Title"
        value={contact.role}
        onChange={(e) => onUpdate("role", e.target.value)}
      />
    </Grid>
    <Grid item xs={12}>
      <MuiTelInput
        fullWidth size="small" label="Phone Number"
        defaultCountry="US"
        value={contact.phone}
        onChange={(value) => onUpdate("phone", value)}
      />
    </Grid>
  </Grid>
);

// ── dropBorderSx ───────────────────────────────────────────────────────────────
// Returns sx border overrides based on which zone is active.
// Purple = link zone (left/right), Blue = reorder zone (above/below).

const dropBorderSx = (side) => {
  if (!side) return {};
  switch (side) {
    case "left":  return { borderLeft:   "3px solid", borderLeftColor:   "secondary.main" };
    case "right": return { borderRight:  "3px solid", borderRightColor:  "secondary.main" };
    case "above": return { borderTop:    "3px solid", borderTopColor:    "primary.main"   };
    case "below": return { borderBottom: "3px solid", borderBottomColor: "primary.main"   };
    default: return {};
  }
};

// ── SingleCard ─────────────────────────────────────────────────────────────────

const SingleCard = ({
  contact, priority,
  onUpdate, onRemove,
  isDragging, activeSide,
  canLink,
  onDragStart, onDragOver, onDrop, onDragEnd,
}) => {
  const handlePressed = useRef(false);

  return (
    <Paper
      variant="outlined"
      draggable
      onDragStart={(e) => {
        if (!handlePressed.current) { e.preventDefault(); return; }
        handlePressed.current = false;
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const w = rect.width;
        const h = rect.height;
        if (canLink && x < w * 0.22)      onDragOver("left");
        else if (canLink && x > w * 0.78) onDragOver("right");
        else if (y < h / 2)               onDragOver("above");
        else                              onDragOver("below");
      }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      onDragEnd={onDragEnd}
      sx={{
        p: 2,
        opacity: isDragging ? 0.35 : 1,
        transition: "opacity 0.15s",
        ...dropBorderSx(activeSide),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DragIndicatorIcon
            onMouseDown={() => { handlePressed.current = true; }}
            sx={{ color: "text.disabled", cursor: "grab", flexShrink: 0 }}
          />
          <Chip label={`Priority ${priority}`} size="small" color="primary" variant="outlined" />
        </Box>
        <IconButton size="small" color="error" onClick={onRemove}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      <ContactFields contact={contact} onUpdate={onUpdate} />
    </Paper>
  );
};

// ── PairRow ────────────────────────────────────────────────────────────────────
// Two linked contacts side-by-side. The pair moves as a unit.

const PairRow = ({
  contacts, priority,
  onUpdateContact, onRemoveContact, onUnlink,
  isDragging, activeSide,
  onDragStart, onDragOver, onDrop, onDragEnd,
}) => {
  const handlePressed = useRef(false);

  return (
    <Paper
      variant="outlined"
      draggable
      onDragStart={(e) => {
        if (!handlePressed.current) { e.preventDefault(); return; }
        handlePressed.current = false;
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const h = rect.height;
        onDragOver(y < h / 2 ? "above" : "below");
      }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      onDragEnd={onDragEnd}
      sx={{
        p: 2,
        opacity: isDragging ? 0.35 : 1,
        transition: "opacity 0.15s",
        ...dropBorderSx(activeSide),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DragIndicatorIcon
            onMouseDown={() => { handlePressed.current = true; }}
            sx={{ color: "text.disabled", cursor: "grab", flexShrink: 0 }}
          />
          <Chip label={`Priority ${priority} — called together`} size="small" color="secondary" variant="outlined" />
        </Box>
        <Tooltip title="Unlink these contacts">
          <IconButton size="small" onClick={onUnlink}>
            <LinkOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        {contacts.map((contact) => (
          <Box key={contact.id} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
              <IconButton size="small" color="error" onClick={() => onRemoveContact(contact.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <ContactFields
              contact={contact}
              onUpdate={(field, value) => onUpdateContact(contact.id, field, value)}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ── ContactListEditor ──────────────────────────────────────────────────────────

const ContactListEditor = ({ list, onUpdateContacts }) => {
  // draggingId as BOTH state (for re-renders) and ref (for synchronous reads in handlers)
  const [draggingId, setDraggingId] = useState(null);
  const draggingIdRef = useRef(null);
  const [dropZone, setDropZone] = useState(null);
  const dropZoneRef = useRef(null);

  const startDragging = (id) => {
    draggingIdRef.current = id;
    setDraggingId(id);
    dropZoneRef.current = null;
    setDropZone(null);
  };

  const stopDragging = () => {
    draggingIdRef.current = null;
    setDraggingId(null);
    dropZoneRef.current = null;
    setDropZone(null);
  };

  const setDrop = (zone) => {
    dropZoneRef.current = zone;
    setDropZone(zone);
  };

  // ── helpers ──

  const updateContact = (contactId, field, value) =>
    onUpdateContacts(
      list.id,
      list.contacts.map((c) => (c.id === contactId ? { ...c, [field]: value } : c))
    );

  const removeContact = (contactId) => {
    const contact = list.contacts.find((c) => c.id === contactId);
    let next = list.contacts.filter((c) => c.id !== contactId);
    if (contact?.linkedWith) {
      next = next.map((c) =>
        c.id === contact.linkedWith ? { ...c, linkedWith: null } : c
      );
    }
    onUpdateContacts(list.id, next);
  };

  const unlinkPair = (contactId) => {
    const partner = list.contacts.find((c) => c.id === contactId)?.linkedWith;
    onUpdateContacts(
      list.id,
      list.contacts.map((c) =>
        c.id === contactId || c.id === partner ? { ...c, linkedWith: null } : c
      )
    );
  };

  // ── drag & drop ──

  const handleDragOver = (targetId, side) => {
    const did = draggingIdRef.current;
    if (!did || did === targetId) return;
    setDrop({ id: targetId, side });
  };

  const handleDrop = (targetId) => {
    const did = draggingIdRef.current;
    const zone = dropZoneRef.current;

    if (!did || !zone || zone.id !== targetId) {
      stopDragging();
      return;
    }

    const { side } = zone;
    const draggingContact = list.contacts.find((c) => c.id === did);
    const targetContact   = list.contacts.find((c) => c.id === targetId);

    if (
      (side === "left" || side === "right") &&
      !draggingContact?.linkedWith &&
      !targetContact?.linkedWith
    ) {
      // ── Link ──
      let next = list.contacts.map((c) => {
        if (c.id === did)       return { ...c, linkedWith: targetId };
        if (c.id === targetId) return { ...c, linkedWith: did };
        return c;
      });
      // Move dragged contact adjacent to target
      const moved = next.find((c) => c.id === did);
      next = next.filter((c) => c.id !== did);
      const tIdx = next.findIndex((c) => c.id === targetId);
      next.splice(side === "left" ? tIdx : tIdx + 1, 0, moved);
      onUpdateContacts(list.id, next);
    } else {
      // ── Reorder ──
      // If dragging a pair, move both contacts as a unit
      const partnerIds = draggingContact?.linkedWith
        ? [did, draggingContact.linkedWith]
        : [did];

      const movedContacts = partnerIds
        .map((pid) => list.contacts.find((c) => c.id === pid))
        .filter(Boolean);

      let next = list.contacts.filter((c) => !partnerIds.includes(c.id));
      const tIdx = next.findIndex((c) => c.id === targetId);
      const insertAt = side === "above" ? tIdx : tIdx + 1;
      next.splice(Math.max(0, insertAt), 0, ...movedContacts);
      onUpdateContacts(list.id, next);
    }

    stopDragging();
  };

  const handleDragEnd = () => stopDragging();

  const handleAddContact = () =>
    onUpdateContacts(list.id, [
      ...list.contacts,
      { id: uuidv4(), name: "", role: "", phone: "", linkedWith: null },
    ]);

  // Disable link zones when the thing being dragged is already in a pair
  const draggingIsLinked = !!(
    draggingIdRef.current &&
    list.contacts.find((c) => c.id === draggingIdRef.current)?.linkedWith
  );

  const rows = buildRows(list.contacts);

  return (
    <Box>
      {rows.map((row, rowIdx) => {
        if (row.type === "pair") {
          const repId = row.contacts[0].id;
          const activeSide =
            dropZone?.id === repId || dropZone?.id === row.contacts[1].id
              ? dropZone.side
              : null;
          const isDraggingRow = row.contacts.some((c) => c.id === draggingId);

          return (
            <Box key={repId} sx={{ mb: 2 }}>
              <PairRow
                contacts={row.contacts}
                priority={rowIdx + 1}
                onUpdateContact={updateContact}
                onRemoveContact={removeContact}
                onUnlink={() => unlinkPair(row.contacts[0].id)}
                isDragging={isDraggingRow}
                activeSide={activeSide}
                onDragStart={() => startDragging(repId)}
                onDragOver={(side) => handleDragOver(repId, side)}
                onDrop={() => handleDrop(repId)}
                onDragEnd={handleDragEnd}
              />
            </Box>
          );
        }

        // single
        const { contact } = row;
        const activeSide = dropZone?.id === contact.id ? dropZone.side : null;
        const canLink = !draggingIsLinked && !contact.linkedWith;

        return (
          <Box key={contact.id} sx={{ mb: 2 }}>
            <SingleCard
              contact={contact}
              priority={rowIdx + 1}
              onUpdate={(field, value) => updateContact(contact.id, field, value)}
              onRemove={() => removeContact(contact.id)}
              isDragging={draggingId === contact.id}
              activeSide={activeSide}
              canLink={canLink}
              onDragStart={() => startDragging(contact.id)}
              onDragOver={(side) => handleDragOver(contact.id, side)}
              onDrop={() => handleDrop(contact.id)}
              onDragEnd={handleDragEnd}
            />
          </Box>
        );
      })}

      <Button
        startIcon={<AddIcon />}
        size="small"
        variant="outlined"
        onClick={handleAddContact}
      >
        Add Contact
      </Button>
    </Box>
  );
};

// ── PropertySettings ───────────────────────────────────────────────────────────

const PropertySettings = () => {
  const { selectedProperty, selectedClient, propertyInformation } = useContext(PropertyContext);
  const [tab, setTab] = useState(0);

  // Property Details
  const [displayName, setDisplayName] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [propertyLLC, setPropertyLLC] = useState("");
  const [propertySalesTax, setPropertySalesTax] = useState("");
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [timezone, setTimezone] = useState("");
  // Geographic coordinates for the superuser portfolio globe (Clients dashboard).
  const [locationLat, setLocationLat] = useState("");
  const [locationLng, setLocationLng] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState("");
  // Last address we successfully geocoded, so blur doesn't re-call the API when
  // the address is unchanged.
  const lastGeocodedAddress = useRef("");
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedSalesUrl, setCopiedSalesUrl] = useState(false);

  // Sales Call Config
  const [salesTwilioNumber, setSalesTwilioNumber] = useState("");
  const [officePhoneNumber, setOfficePhoneNumber] = useState("");


  // Guest Escalation
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [callLists, setCallLists] = useState([]);
  const [schedule, setSchedule] = useState(buildDefaultSchedule);
  const [escalationSaving, setEscalationSaving] = useState(false);
  const [escalationSaved, setEscalationSaved] = useState(false);
  const [newListId, setNewListId] = useState(null);

  useEffect(() => {
    if (!propertyInformation) return;
    setDisplayName(propertyInformation.displayName || "");
    setPropertyAddress(propertyInformation.propertyAddress || "");
    lastGeocodedAddress.current = propertyInformation.propertyAddress || "";
    setPropertyLLC(propertyInformation.propertyLLC || "");
    setPropertySalesTax(propertyInformation.propertySalesTax || "");
    setTimezone(propertyInformation.timezone || "");
    setLocationLat(
      propertyInformation.location?.lat != null
        ? String(propertyInformation.location.lat)
        : ""
    );
    setLocationLng(
      propertyInformation.location?.lng != null
        ? String(propertyInformation.location.lng)
        : ""
    );
    setSalesTwilioNumber(propertyInformation.salesConfig?.salesTwilioNumber || "");
    setOfficePhoneNumber(propertyInformation.salesConfig?.officePhoneNumber || "");

    const ge = propertyInformation.guestEscalation;
    if (ge) {
      setEscalationEnabled(ge.enabled || false);
      setCallLists(
        (ge.callLists || []).map((l) => ({
          initialMessage: "",
          ...l,
          contacts: normalizeContacts(l.contacts),
        }))
      );
      if (ge.schedule) setSchedule((prev) => ({ ...prev, ...ge.schedule }));
    }
  }, [propertyInformation]);

  const handleSaveDetails = async () => {
    setDetailsSaving(true);
    try {
      // Parse coordinates; store a location object only when both are valid
      // numbers, otherwise clear it (null) so the globe simply omits the point.
      const latNum = parseFloat(locationLat);
      const lngNum = parseFloat(locationLng);
      const location =
        Number.isFinite(latNum) && Number.isFinite(lngNum)
          ? { lat: latNum, lng: lngNum }
          : null;
      await updateDoc(propertyDoc(selectedClient, selectedProperty), {
        displayName, propertyAddress, propertyLLC, propertySalesTax, timezone,
        location,
        salesConfig: { salesTwilioNumber, officePhoneNumber },
      });
      setDetailsSaved(true);
      setTimeout(() => setDetailsSaved(false), 3000);
    } catch (err) {
      console.error("Error saving property details:", err);
    }
    setDetailsSaving(false);
  };

  // On blur of the address field, geocode it once (if it changed) and fill in
  // lat/lng. The user can still override the coordinates manually afterward.
  const handleAddressBlur = async () => {
    const address = propertyAddress.trim();
    setGeocodeError("");
    if (!address || address === lastGeocodedAddress.current) return;

    setGeocoding(true);
    try {
      const geocode = httpsCallable(getFunctions(), "geocodeAddress");
      const { data } = await geocode({ address });
      lastGeocodedAddress.current = address;
      if (data?.found) {
        setLocationLat(String(data.lat));
        setLocationLng(String(data.lng));
      } else {
        setGeocodeError("No coordinates found for that address.");
      }
    } catch (err) {
      console.error("Error geocoding address:", err);
      setGeocodeError("Couldn't look up coordinates. Enter them manually.");
    }
    setGeocoding(false);
  };

  const addCallList = () => {
    const id = uuidv4();
    setCallLists([...callLists, {
      id,
      name: "",
      initialMessage: "",
      contacts: [{ id: uuidv4(), name: "", role: "", phone: "", linkedWith: null }],
    }]);
    setNewListId(id);
  };

  const removeCallList = (listId) => {
    setCallLists(callLists.filter((l) => l.id !== listId));
    setSchedule((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        const key = d.toLowerCase();
        if (next[key].daytimeListId === listId) next[key] = { ...next[key], daytimeListId: "" };
        if (next[key].afterHoursListId === listId) next[key] = { ...next[key], afterHoursListId: "" };
      });
      return next;
    });
  };

  const updateListName = (listId, name) =>
    setCallLists(callLists.map((l) => (l.id === listId ? { ...l, name } : l)));

  const updateListInitialMessage = (listId, initialMessage) =>
    setCallLists(callLists.map((l) => (l.id === listId ? { ...l, initialMessage } : l)));

  const updateListContacts = (listId, contacts) =>
    setCallLists(callLists.map((l) => (l.id === listId ? { ...l, contacts } : l)));

  const updateSchedule = (day, field, value) =>
    setSchedule((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));

  const handleSaveEscalation = async () => {
    setEscalationSaving(true);
    try {
      await updateDoc(propertyDoc(selectedClient, selectedProperty), {
        guestEscalation: { enabled: escalationEnabled, callLists, schedule },
      });
      setEscalationSaved(true);
      setTimeout(() => setEscalationSaved(false), 3000);
    } catch (err) {
      console.error("Error saving guest escalation settings:", err);
    }
    setEscalationSaving(false);
  };

  const listOptions = callLists.filter((l) => l.name.trim() !== "");

  return (
    <Box sx={{ p: 3, maxWidth: tab === 2 ? 1200 : 900, mx: "auto" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Property Settings</Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Property Details" />
          <Tab icon={<NightsStayIcon />} iconPosition="start" label="Guest Escalation Call List" />
          <Tab icon={<MapIcon />} iconPosition="start" label="Property Map" />
        </Tabs>
      </Box>

      {/* ── Property Details ── */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Property Display Name" value={displayName}
              onChange={(e) => setDisplayName(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Property Address" value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              onBlur={handleAddressBlur}
              helperText={
                geocoding
                  ? "Looking up coordinates…"
                  : geocodeError || " "
              }
              error={Boolean(geocodeError)}
              InputProps={{
                endAdornment: geocoding ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : null,
              }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Property LLC" value={propertyLLC}
              onChange={(e) => setPropertyLLC(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Sales Tax Rate" value={propertySalesTax}
              onChange={(e) => setPropertySalesTax(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                label="Timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <MenuItem value=""><em>Select timezone</em></MenuItem>
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* ── Map Location (portfolio globe) ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
              Map Location
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Latitude / longitude used to plot this property on the client portfolio globe.
              These are filled in automatically from the address above, but you can override them
              here. Leave blank to omit it from the globe.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Latitude"
              value={locationLat}
              onChange={(e) => setLocationLat(e.target.value)}
              placeholder="e.g. 36.1699"
              inputProps={{ step: "any" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Longitude"
              value={locationLng}
              onChange={(e) => setLocationLng(e.target.value)}
              placeholder="e.g. -115.1398"
              inputProps={{ step: "any" }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Twilio Webhook URL"
              value={`https://us-central1-siegel-room-status.cloudfunctions.net/twilioCallWebhook?propertyId=${selectedProperty}&clientId=${selectedClient}`}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copiedUrl ? "Copied!" : "Copy URL"}>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `https://us-central1-siegel-room-status.cloudfunctions.net/twilioCallWebhook?propertyId=${selectedProperty}&clientId=${selectedClient}`
                          );
                          setCopiedUrl(true);
                          setTimeout(() => setCopiedUrl(false), 2000);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" color={copiedUrl ? "primary" : "inherit"} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              helperText="Paste this URL into Twilio's 'A call comes in' webhook for this property's phone number."
            />
          </Grid>

          {/* ── Sales Call Configuration ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
              Sales Call Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Used for click-to-call outbound dialing and inbound lead notifications on the Call List page.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MuiTelInput
              fullWidth
              label="Sales Twilio Number"
              defaultCountry="US"
              value={salesTwilioNumber}
              onChange={(value) => setSalesTwilioNumber(value)}
              helperText="Your second Twilio number used for the sales line."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MuiTelInput
              fullWidth
              label="Office Phone Number"
              defaultCountry="US"
              value={officePhoneNumber}
              onChange={(value) => setOfficePhoneNumber(value)}
              helperText="The physical office phone that rings for click-to-call and inbound forwarding."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sales Twilio Webhook URL"
              value={`https://us-central1-siegel-room-status.cloudfunctions.net/salesCallWebhook?action=inbound&propertyId=${selectedProperty}&clientId=${selectedClient}`}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copiedSalesUrl ? "Copied!" : "Copy URL"}>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `https://us-central1-siegel-room-status.cloudfunctions.net/salesCallWebhook?action=inbound&propertyId=${selectedProperty}&clientId=${selectedClient}`
                          );
                          setCopiedSalesUrl(true);
                          setTimeout(() => setCopiedSalesUrl(false), 2000);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" color={copiedSalesUrl ? "primary" : "inherit"} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              helperText="Paste this URL into your sales Twilio number's 'A call comes in' webhook."
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" onClick={handleSaveDetails} disabled={detailsSaving}>
              {detailsSaved ? "Saved!" : detailsSaving ? "Saving..." : "Save Changes"}
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── Guest Escalation Call List ── */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch checked={escalationEnabled}
                onChange={(e) => setEscalationEnabled(e.target.checked)} color="primary" />
            }
            label={<Typography variant="body1">Guest Escalation Active</Typography>}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            When enabled, on-call contacts are available for guest call routing based on
            the schedule below.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box>
            <Typography variant="h6">Call Lists</Typography>
            <Typography variant="caption" color="text.secondary">
              Drag a contact left or right onto another to link them (called together).
              Drag up or down to reorder priority.
            </Typography>
          </Box>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addCallList}>
            New List
          </Button>
        </Box>

        {callLists.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 3, mb: 2 }}>
            No call lists yet. Create one to get started.
          </Typography>
        )}

        {callLists.map((list) => (
          <Accordion key={list.id} variant="outlined" sx={{ mb: 1 }} defaultExpanded={list.id === newListId}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", pr: 1 }}>
                <TextField
                  size="small" value={list.name} placeholder="List name"
                  autoFocus={list.id === newListId}
                  onChange={(e) => { e.stopPropagation(); updateListName(list.id, e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  sx={{ flexGrow: 1 }}
                />
                <Chip
                  label={`${list.contacts.length} contact${list.contacts.length !== 1 ? "s" : ""}`}
                  size="small"
                />
                <IconButton size="small" color="error"
                  onClick={(e) => { e.stopPropagation(); removeCallList(list.id); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                size="small"
                label="Initial call message"
                placeholder="e.g. Thank you for calling. We are connecting you with our on-call team."
                value={list.initialMessage || ""}
                onChange={(e) => updateListInitialMessage(list.id, e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
                helperText="Played once to the caller before dialing the first contact."
              />
              <ContactListEditor list={list} onUpdateContacts={updateListContacts} />
            </AccordionDetails>
          </Accordion>
        ))}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Weekly Schedule</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set daytime hours and assign a call list for each period. Outside the daytime
          window the after-hours list is used — note that a day's after-hours list stays
          active past midnight until the <em>next</em> day's daytime start time. For
          example, if Sunday's daytime ends at 10 pm and Monday's starts at 8 am, Sunday's
          after-hours list covers the full overnight window through 8 am Monday.
        </Typography>

        {DAYS.map((day) => {
          const key = day.toLowerCase();
          const ds = schedule[key] || defaultDaySchedule;
          return (
            <Paper key={day} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>{day}</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" label="Daytime Start" type="time"
                    value={ds.startTime}
                    onChange={(e) => updateSchedule(key, "startTime", e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" label="Daytime End" type="time"
                    value={ds.endTime}
                    onChange={(e) => updateSchedule(key, "endTime", e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Daytime List</InputLabel>
                    <Select label="Daytime List" value={ds.daytimeListId}
                      onChange={(e) => updateSchedule(key, "daytimeListId", e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {listOptions.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>After-Hours List</InputLabel>
                    <Select label="After-Hours List" value={ds.afterHoursListId}
                      onChange={(e) => updateSchedule(key, "afterHoursListId", e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {listOptions.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          );
        })}

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={handleSaveEscalation} disabled={escalationSaving}>
            {escalationSaved ? "Saved!" : escalationSaving ? "Saving..." : "Save Escalation Settings"}
          </Button>
        </Box>
      </TabPanel>

      {/* ── Property Map ── */}
      <TabPanel value={tab} index={2}>
        <PropertyMap />
      </TabPanel>
    </Box>
  );
};

export default PropertySettings;
